// Section Validation Utility - validates song sections against structural rules
// Provides default rules per section type and returns structured validation results

import type { SectionType } from '../components/lexical/nodes/SectionBlockNode'

export interface SectionValidationRule {
  minLines?: number
  maxLines?: number
  recommendedLines?: number
  expectedRhymePattern?: string  // e.g., "AABB", "ABAB", "ABCB"
}

export interface ValidationIssue {
  type: 'error' | 'warning'
  message: string
}

export interface ValidationResult {
  isValid: boolean
  issues: ValidationIssue[]
  suggestions: string[]
}

/**
 * Default validation rules per section type.
 * Based on common songwriting conventions:
 * - Verses: 2-8 lines (typically 4), ABAB alternating rhyme pattern
 * - Chorus: 2-6 lines (typically 4), AABB couplet rhyme (catchy, memorable)
 * - Bridge: 2-4 lines (typically 2), AABB or free (bridges break patterns for contrast)
 * - Outro/Intro: 1-4 lines (flexible, no strict rhyme pattern)
 */
export const DEFAULT_SECTION_RULES: Record<SectionType, SectionValidationRule> = {
  verse: { minLines: 2, maxLines: 8, recommendedLines: 4, expectedRhymePattern: 'ABAB' },
  chorus: { minLines: 2, maxLines: 6, recommendedLines: 4, expectedRhymePattern: 'AABB' },
  bridge: { minLines: 2, maxLines: 4, recommendedLines: 2, expectedRhymePattern: 'AABB' },
  outro: { minLines: 1, maxLines: 4 },  // No pattern expected
  intro: { minLines: 1, maxLines: 4 },  // No pattern expected
}

/**
 * Validates a section against structural rules.
 *
 * @param sectionType - Type of section (verse, chorus, etc.)
 * @param lineCount - Number of lines in the section
 * @param rules - Optional custom rules (defaults to DEFAULT_SECTION_RULES)
 * @param actualRhymePattern - Optional actual rhyme pattern from section (e.g., "AABB")
 * @param customValidator - Optional custom validation function for extensibility
 * @returns ValidationResult with validity status, issues, and suggestions
 */
export function validateSection(
  sectionType: SectionType,
  lineCount: number,
  rules?: SectionValidationRule,
  actualRhymePattern?: string,
  customValidator?: (lineCount: number, rhymePattern?: string) => ValidationIssue[]
): ValidationResult {
  const validationRules = rules || DEFAULT_SECTION_RULES[sectionType]
  const issues: ValidationIssue[] = []
  const suggestions: string[] = []

  // Validate minimum lines
  if (validationRules.minLines !== undefined && lineCount < validationRules.minLines) {
    issues.push({
      type: 'error',
      message: `Too few lines: ${lineCount} (minimum: ${validationRules.minLines})`,
    })
    suggestions.push(`Add ${validationRules.minLines - lineCount} more line(s)`)
  }

  // Validate maximum lines
  if (validationRules.maxLines !== undefined && lineCount > validationRules.maxLines) {
    issues.push({
      type: 'warning',
      message: `Many lines: ${lineCount} (recommended max: ${validationRules.maxLines})`,
    })
    suggestions.push('Consider splitting into multiple sections')
  }

  // Validate rhyme pattern if expected pattern is defined
  if (validationRules.expectedRhymePattern && actualRhymePattern) {
    if (validationRules.expectedRhymePattern !== actualRhymePattern) {
      issues.push({
        type: 'warning',
        message: `Rhyme pattern mismatch: expected ${validationRules.expectedRhymePattern}, got ${actualRhymePattern}`,
      })
      suggestions.push(`Consider adjusting rhyme scheme to match ${validationRules.expectedRhymePattern}`)
    }
  }

  // Call custom validator if provided
  if (customValidator) {
    const customIssues = customValidator(lineCount, actualRhymePattern)
    issues.push(...customIssues)
  }

  // Check if line count matches recommendation
  if (
    validationRules.recommendedLines !== undefined &&
    lineCount !== validationRules.recommendedLines &&
    issues.length === 0 // Only suggest if no errors/warnings
  ) {
    const diff = lineCount - validationRules.recommendedLines
    if (Math.abs(diff) > 0) {
      suggestions.push(
        `Typical ${sectionType} has ${validationRules.recommendedLines} lines (you have ${lineCount})`
      )
    }
  }

  return {
    isValid: issues.filter((i) => i.type === 'error').length === 0,
    issues,
    suggestions,
  }
}

/**
 * Gets a human-readable summary of validation results.
 *
 * @param result - Validation result to summarize
 * @returns String summary suitable for tooltips/UI
 */
export function getValidationSummary(result: ValidationResult): string {
  if (result.isValid && result.issues.length === 0 && result.suggestions.length === 0) {
    return 'Section structure looks good'
  }

  const parts: string[] = []

  result.issues.forEach((issue) => {
    parts.push(issue.message)
  })

  result.suggestions.forEach((suggestion) => {
    parts.push(suggestion)
  })

  return parts.join('\n')
}
